import { create } from 'zustand'

import { streamChat } from '@/lib/api'
import type { ChatMessage } from '@/types/chat'

type Status = 'idle' | 'sending' | 'error'

interface ChatState {
  messages: ChatMessage[]
  conversationId?: string
  status: Status
  error?: string
  send: (text: string) => Promise<void>
  stop: () => void
  reset: () => void
}

let abort: AbortController | null = null

const uid = () => Math.random().toString(36).slice(2, 10)

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  status: 'idle',
  error: undefined,

  async send(text) {
    const content = text.trim()
    if (!content || get().status === 'sending') return

    const user: ChatMessage = { id: uid(), role: 'user', content, createdAt: Date.now() }
    const reply: ChatMessage = {
      id: uid(),
      role: 'assistant',
      content: '',
      createdAt: Date.now(),
      streaming: true,
    }
    const history = [...get().messages, user]
    set({ messages: [...history, reply], status: 'sending', error: undefined })

    abort = new AbortController()
    try {
      await streamChat(
        { messages: history, conversationId: get().conversationId },
        {
          signal: abort.signal,
          onToken: (token) =>
            set((s) => ({
              messages: s.messages.map((m) =>
                m.id === reply.id ? { ...m, content: m.content + token } : m,
              ),
            })),
          onDone: (evt) => set({ conversationId: evt.conversationId }),
        },
      )
      set((s) => ({
        messages: s.messages.map((m) => (m.id === reply.id ? { ...m, streaming: false } : m)),
        status: 'idle',
      }))
    } catch (err) {
      const aborted = (err as Error).name === 'AbortError'
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === reply.id ? { ...m, streaming: false } : m,
        ),
        status: aborted ? 'idle' : 'error',
        error: aborted ? undefined : (err as Error).message,
      }))
    } finally {
      abort = null
    }
  },

  stop() {
    abort?.abort()
  },

  reset() {
    abort?.abort()
    set({ messages: [], conversationId: undefined, status: 'idle', error: undefined })
  },
}))
